import { ILabelStyleProps, ILabelStyles } from "@fluentui/react/lib/Label";
import {
  AnimationClassNames,
  DefaultFontStyles,
  FontSizes,
  HighContrastSelector,
  IStyle,
  normalize,
} from "@fluentui/react/lib/Styling";
import {
  ITextFieldStyleProps,
  ITextFieldStyles,
} from "@fluentui/react/lib/TextField";
import { IStyleFunctionOrObject } from "@fluentui/react/lib/Utilities";

import {
  commonHighContrastDisabledStyles,
  commonHighContrastOutlineHoverFocus,
  controlPalette,
  getStandardControlSemanticColorsWithTheme,
  StandardControlSemanticColors,
  textContainerClassName,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

function getLabelStyles(
  underlined?: boolean,
  disabled?: boolean,
  focused?: boolean
): IStyleFunctionOrObject<ILabelStyleProps, ILabelStyles> {
  return () => ({
    root: [
      textContainerClassName,
      underlined &&
        disabled && {
          color: controlPalette.textDisabledColor,
        },
      underlined && {
        fontSize: FontSizes.medium,
        marginRight: 8,
        paddingLeft: 12,
        paddingRight: 0,
        lineHeight: "22px",
        height: 32,
      },
      underlined &&
        focused && {
          selectors: {
            [HighContrastSelector]: {
              height: 31, // -1px to prevent jumpiness in HC with the increased border-width to 2px
            },
          },
        },
      {
        fontWeight: 400,
      },
    ],
  });
}

export function getStyles(props: ITextFieldStyleProps): ITextFieldStyles {
  const {
    theme,
    className,
    disabled,
    focused,
    required,
    multiline,
    hasLabel,
    borderless,
    underlined,
    hasIcon,
    resizable,
    hasErrorMessage,
    inputClassName,
    autoAdjustHeight,
  } = props;

  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  const semanticColors = {
    disabledBackground: isThemingEnabled()
      ? standardControlSemanticColors.disabledBackgroundColor
      : controlPalette.defaultBackgroundColor,
    inputPlaceholderText: isThemingEnabled()
      ? standardControlSemanticColors.textColor
      : controlPalette.textColor,
    disabledText: isThemingEnabled()
      ? standardControlSemanticColors.textDisabledColor
      : "#a6a6a6",
    errorText: "#a80000", // TODO: Theming for error colors (non-neutral, non app-specific/primary)
    inputBorderHovered: isThemingEnabled()
      ? standardControlSemanticColors.inputHoverBorderColor
      : "#797775",
    inputFocusBorderAlt: isThemingEnabled()
      ? standardControlSemanticColors.inputFocusBorderColor
      : "#605E5C",
    inputBorder: standardControlSemanticColors.inputBorderColor,
    inputBackground: "transparent",
    inputText: isThemingEnabled()
      ? standardControlSemanticColors.textColor
      : controlPalette.textColor,
    bodySubtext: isThemingEnabled()
      ? standardControlSemanticColors.textColor
      : controlPalette.textColor,
  };

  const fieldPrefixSuffix: IStyle = {
    background: semanticColors.disabledBackground, // Suffix/Prefix are not editable so the disabled slot perfectly fits.
    color: !disabled
      ? semanticColors.inputPlaceholderText
      : semanticColors.disabledText,
    display: "flex",
    alignItems: "center",
    padding: "0 10px",
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  return {
    root: [
      normalize,
      {
        position: "relative",
      },
      className,
    ],
    wrapper: [
      underlined && {
        display: "flex",
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
        borderBottomColor: semanticColors.inputBorder,
        width: "100%",
      },
      hasErrorMessage &&
        underlined &&
        !disabled && {
          borderBottomColor: semanticColors.errorText,
          selectors: {
            ":hover": {
              borderBottomColor: semanticColors.errorText,
              selectors: {
                [HighContrastSelector]: {
                  borderBottomColor: "Highlight",
                },
              },
            },
          },
        },
      underlined &&
        disabled && {
          borderBottomColor: semanticColors.disabledBackground,
        },
      underlined &&
        !disabled &&
        !focused &&
        !hasErrorMessage && {
          selectors: {
            ":hover": {
              borderBottomColor: semanticColors.inputBorderHovered,
              selectors: {
                [HighContrastSelector]: {
                  borderBottomColor: "Highlight",
                },
              },
            },
          },
        },
      underlined &&
        focused && {
          borderBottomColor: !hasErrorMessage
            ? semanticColors.inputFocusBorderAlt
            : semanticColors.errorText,
          selectors: {
            [HighContrastSelector]: {
              borderBottomWidth: 2,
              borderBottomColor: "Highlight",
            },
          },
        },
    ],
    fieldGroup: [
      normalize,
      {
        border: `1px solid ${semanticColors.inputBorder}`,
        background: semanticColors.inputBackground,
        cursor: "text",
        height: 32,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        position: "relative",
        selectors: {
          ":hover": !disabled && commonHighContrastOutlineHoverFocus,
        },
      },
      multiline && {
        minHeight: "60px",
        height: "auto",
        display: "flex",
      },
      borderless && {
        border: "none",
      },
      !focused &&
        !disabled && {
          selectors: {
            ":hover": {
              borderColor: semanticColors.inputBorderHovered,
            },
          },
        },
      focused && [
        {
          borderColor: semanticColors.inputFocusBorderAlt,
        },
        commonHighContrastOutlineHoverFocus,
      ],
      disabled && [
        {
          backgroundColor: semanticColors.disabledBackground,
          borderColor: semanticColors.disabledBackground,
          cursor: "default",
          selectors: {
            [HighContrastSelector]: {
              backgroundColor: "transparent",
            },
          },
        },
        commonHighContrastDisabledStyles,
      ],
      underlined && {
        flex: "1 1 0px",
        border: "none",
        textAlign: "left",
      },
      underlined &&
        focused && {
          selectors: {
            [HighContrastSelector]: {
              height: 31, // -1px to prevent jumpiness in HC with the increased border-width to 2px
            },
          },
        },
      underlined &&
        disabled && {
          backgroundColor: "transparent",
        },
      hasErrorMessage && {
        borderColor: semanticColors.errorText,
        selectors: {
          "&:focus, &:hover": {
            borderColor: semanticColors.errorText,
          },
        },
      },
      hasErrorMessage &&
        focused && {
          borderColor: semanticColors.errorText,
        },
      !hasLabel &&
        required && {
          selectors: {
            ":after": {
              content: `'*'`,
              color: semanticColors.errorText,
              position: "absolute",
              top: -5,
              right: -10,
            },
            [HighContrastSelector]: {
              selectors: {
                ":after": {
                  right: -14, // moving the * 4 pixel to right to alleviate border clipping in HC mode.
                },
              },
            },
          },
        },
    ],
    field: [
      normalize,
      {
        fontSize: FontSizes.medium,
        borderRadius: 0,
        border: "none",
        background: "none",
        backgroundColor: "transparent",
        color: semanticColors.inputText,
        padding: "0 12px",
        width: "100%",
        minWidth: 0,
        textOverflow: "ellipsis",
        outline: 0,
        selectors: {
          "&:active, &:focus, &:hover": { outline: 0 },
          "::-ms-clear": {
            display: "none",
          },
          "::placeholder": {
            color: semanticColors.inputPlaceholderText,
            opacity: 1,
          },
          ":-ms-input-placeholder": {
            color: semanticColors.inputPlaceholderText,
            opacity: 1,
          },
        },
      },
      multiline &&
        !resizable && [
          {
            resize: "none",
          },
        ],
      multiline && {
        minHeight: "inherit",
        lineHeight: 17,
        flexGrow: 1,
        paddingTop: 6,
        overflow: "auto",
        width: "100%",
      },
      multiline &&
        autoAdjustHeight && {
          overflow: "hidden",
        },
      hasIcon && {
        paddingRight: 24,
      },
      multiline &&
        hasIcon && {
          paddingRight: 40,
        },
      disabled && {
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: semanticColors.disabledText,
        selectors: {
          "::placeholder": {
            color: semanticColors.disabledText,
          },
          ":-ms-input-placeholder": {
            color: semanticColors.disabledText,
          },
          [HighContrastSelector]: {
            "::placeholder": {
              color: "GrayText",
            },
            ":-ms-input-placeholder": {
              color: "GrayText",
            },
          },
        },
      },
      underlined && {
        textAlign: "left",
      },
      focused &&
        !borderless && {
          selectors: {
            [HighContrastSelector]: {
              paddingLeft: 11,
              paddingRight: 11,
            },
          },
        },
      focused &&
        multiline &&
        !borderless && {
          selectors: {
            [HighContrastSelector]: {
              paddingTop: 4, // take into consideration the 2px increased border-width (not when borderless).
            },
          },
        },
      inputClassName,
    ],
    icon: [
      multiline && {
        paddingRight: 24,
        paddingBottom: 8,
        alignItems: "flex-end",
      },
      {
        pointerEvents: "none",
        position: "absolute",
        bottom: 5,
        right: 8,
        top: "auto",
        fontSize: 16,
        lineHeight: 18,
      },
      disabled && {
        color: semanticColors.disabledText,
      },
    ],
    description: [
      {
        color: semanticColors.bodySubtext,
        fontSize: FontSizes.xSmall,
      },
    ],
    errorMessage: [
      AnimationClassNames.slideDownIn20,
      DefaultFontStyles.small,
      {
        color: semanticColors.errorText,
        margin: 0,
        paddingTop: 5,
        display: "flex",
        alignItems: "center",
      },
    ],
    prefix: [fieldPrefixSuffix],
    suffix: [fieldPrefixSuffix],
    subComponentStyles: {
      label: getLabelStyles(underlined, disabled, focused),
    },
    revealButton: {},
    revealSpan: {},
    revealIcon: {},
  };
}
